import React, { useState } from "react";
import CustomCard from "./custom-card";
import { makeStyles } from "@material-ui/core";
import moment from "moment";
import { cardTitleStyle, cardContentStyle, cardInlineInfoStyle } from "./card-styles";
import { EditIcon, DuplicateIcon } from "./icons-svg";
import CustomModal from "./custom-modal";
import EngagementForm from "./engagement-form";
import store from "src/store";
import Tooltip from "./tooltip";
import copyToClipboard from "copy-to-clipboard";
import useIsLoggedIn from "src/utils/use-is-logged-in";

const useStyles = makeStyles({
  root: {
    display: 'flex',
    boxShadow: 'none',
    padding: '20px',
    alignItems: 'center'
  },
  child: {
    flex: '1',
    padding: '5px'
  },
  rowParent: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%'
  },
  content: {
    flex: '1 0 auto',
    height: '100%',
    padding: '0px !important'
  },
  rightIcon: {
    float: 'right'
  },
  staticText: {
    color: '#717178'
  },
  icon: {
    display: 'inline-block',
    fontSize: '18px',
    color: '#2E2E38',
    marginLeft: '5px'
  },
  clickable: {
    cursor: 'pointer',
  }
});

export default function EngagementCard(props) {
  const { userInfo } = useIsLoggedIn();
  const classes = useStyles();

  const defaultCopyTip = "Copy to clipboard";
  const [copyTip, setCopyTip] = useState(defaultCopyTip);

  const [showEditEngagementModal, setShowEditEngagementModal] = useState(false);

  const [orgId, setOrgId] = useState('');
  const [engId, setEngId] = useState('');
  const [maxAllowedVersion, setMaxAllowedVersion] = useState(3);
  const [allowedEmails, setAllowedEmails] = useState([]);
  const [API_KEY, setAPI_KEY] = useState('');

  const [orgIdHasError, setOrgIdHasError] = useState(false);
  const [orgIdErrorText, setOrgIdErrorText] = useState('');
  const [engIdHasError, setEngIdHasError] = useState(false);
  const [engIdErrorText, setEngIdErrorText] = useState('');
  const [maxAllowedVersionHasError, setMaxAllowedVersionHasError] = useState(false);
  const [maxAllowedVersionErrorText, setMaxAllowedVersionErrorText] = useState('');

  const resetEngagementForm = () => {
    setOrgId('');
    setEngId('');
    setMaxAllowedVersion(3);
    setAllowedEmails([]);
    setOrgIdHasError(false);
    setOrgIdErrorText('');
    setEngIdHasError(false);
    setEngIdErrorText('');
    setMaxAllowedVersionHasError(false);
    setMaxAllowedVersionErrorText('');
  }

  const setEngagementData = () => {
    setOrgId(props.engagement.orgId);
    setEngId(props.engagement.engId);
    setMaxAllowedVersion(props.engagement.maxAllowedVersion);
    setAllowedEmails(props.engagement.allowedEmails);
    setAPI_KEY(props.engagement.API_KEY);
    setOrgIdHasError(false);
    setOrgIdErrorText('');
    setEngIdHasError(false);
    setEngIdErrorText('');
    setMaxAllowedVersionHasError(false);
    setMaxAllowedVersionErrorText('');
  }

  const openEditEngagementModal = () => {
    setEngagementData();
    setShowEditEngagementModal(true);
  }

  const formIsValid = () => {
    let isValid = true;

    if (!userInfo?.auth0Subscripton) {
      if (!orgId) {
        isValid = false;
        setOrgIdHasError(true);
        setOrgIdErrorText("Organisation is required");
      } else {
        setOrgIdHasError(false);
        setOrgIdErrorText("");
      }
    }

    if (!engId) {
      isValid = false;
      setEngIdHasError(true);
      setEngIdErrorText("Engagement id is required");
    } else {
      setEngIdHasError(false);
      setEngIdErrorText("");
    }

    if (engId) {
      let existingEngagementWithSameId = store.engagements.filter((item) => (item.engId == engId && item._id != props.engagement._id));
      if (existingEngagementWithSameId.length) {
        isValid = false;
        setEngIdHasError(true);
        setEngIdErrorText("Engagement id already exists");
      } else {
        setEngIdHasError(false);
        setEngIdErrorText("");
      }
    }

    if (!maxAllowedVersion) {
      isValid = false;
      setMaxAllowedVersionHasError(true);
      setMaxAllowedVersionErrorText("Maximum allowed version is required");
    } else {
      setMaxAllowedVersionHasError(false);
      setMaxAllowedVersionErrorText("");
    }

    return isValid;
  }

  const updateEngagement = async () => {
    if (formIsValid()) {
      let engagementData = {
        orgId: orgId,
        engId: engId,
        allowedEmails: allowedEmails,
        maxAllowedVersion: Number(maxAllowedVersion),
        API_KEY: API_KEY
      };

      await store.updateEngagement(engagementData, props.engagement._id);
      store.checkAndSetEngagementManagerFilter();
      setShowEditEngagementModal(false);
      resetEngagementForm();
    }
  }

  const copy = () => {
    copyToClipboard(props.engagement.API_KEY)
    setCopyTip("Copied!")
    setTimeout(() => {
      try { // in timeout might be an error of updating unmounted component
        setCopyTip(defaultCopyTip)
      } catch (err) {
        const noop = function () { }
        noop() // eslint don't allow empty blocks
      }
    }, 2000)
  }

  return (
    <>
      <CustomModal
        size="medium"
        title="Edit engagement"
        showModal={showEditEngagementModal}
        onCloseModal={() => { setShowEditEngagementModal(false) }}
        submitBtnText={'Update'}
        onSubmit={() => { updateEngagement() }}
        submitBtnDisabled={false}
      >
        <EngagementForm
          orgId={orgId}
          engId={engId}
          maxAllowedVersion={maxAllowedVersion}
          allowedEmails={allowedEmails}
          API_KEY={API_KEY}
          setOrgId={(value) => setOrgId(value)}
          setEngId={(value) => setEngId(value)}
          setMaxAllowedVersion={(value) => setMaxAllowedVersion(value)}
          setAllowedEmails={(value) => setAllowedEmails(value)}
          setAPI_KEY={(value) => setAPI_KEY(value)}
          orgIdHasError={orgIdHasError}
          engIdHasError={engIdHasError}
          orgIdErrorText={orgIdErrorText}
          engIdErrorText={engIdErrorText}
          maxAllowedVersionHasError={maxAllowedVersionHasError}
          maxAllowedVersionErrorText={maxAllowedVersionErrorText}
          isEdit={true}
        />
      </CustomModal>

      <CustomCard className={classes.root}>
        <div css={`flex-grow:1;`}>
          <div className={classes.rowParent}>
            <div>
              <div
                css={`
                    color:inherit;
                    text-decoration:inherit;
                    font-size:18px;
                  `}
              >
                <div>
                  <span className={classes.rightIcon}>
                    <span className={`${classes.icon} ${classes.clickable}`} onClick={() => { openEditEngagementModal() }}><Tooltip tip={"Edit engagement"}>{EditIcon()}</Tooltip></span>
                  </span>
                </div>

                <div css={cardInlineInfoStyle}>
                  <div css={cardTitleStyle}>
                    ID:
                  </div>
                  <div css={cardContentStyle}>
                    {props.engagement.engId}
                  </div>
                </div>

                {
                  (!userInfo?.auth0Subscripton && props.engagement.organizationData) &&
                  <div css={cardInlineInfoStyle}>
                    <div css={cardTitleStyle}>
                      Organization:
                    </div>
                    <div css={cardContentStyle}>
                      {props.engagement.organizationData.name}
                    </div>
                  </div>
                }

                <div css={cardInlineInfoStyle}>
                  <div css={cardTitleStyle}>
                    API Key:
                  </div>
                  <div css={cardContentStyle}>
                    {"*****" || "No API Key available"}
                  </div>
                  {
                    props.engagement.API_KEY ?
                      <div css={cardContentStyle}>
                        <Tooltip tip={copyTip}>
                          <span className={`${classes.icon} ${classes.clickable}`} onClick={() => {
                            copy();
                          }}>{DuplicateIcon()}</span>
                        </Tooltip>
                      </div> : null
                  }

                </div>

                {
                  !userInfo?.auth0Subscripton &&
                  <div css={cardInlineInfoStyle}>
                    <div css={cardTitleStyle}>
                      Allowed Emails:
                    </div>
                    <div css={cardContentStyle}>
                      {props.engagement.allowedEmails?.join?.(', ') || "No one"}
                    </div>
                  </div>
                }

                <div css={cardInlineInfoStyle}>
                  <div css={cardTitleStyle}>
                    Max allowed versions:
                  </div>
                  <div css={cardContentStyle}>
                    {props.engagement.maxAllowedVersion}
                  </div>
                </div>

                <div css={cardInlineInfoStyle}>
                  <div css={cardTitleStyle}>
                    Created on:
                  </div>
                  <div css={cardContentStyle}>
                    {moment(props.engagement.created_on).format("MM-DD-YYYY | hh:mm A")}
                  </div>
                </div>

                <div css={cardInlineInfoStyle}>
                  <div css={cardTitleStyle}>
                    Created by:
                  </div>
                  <div css={cardContentStyle}>
                    {props.engagement.userName}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CustomCard>
    </>
  )

}